// Added By Farhan Khan Khan on 24-Dec-2025
// Extended By System on 29-Jan-2026 - Added Mounting Structure BOM support
/* eslint-disable no-lone-blocks */
import React, { useState, useEffect } from "react";
import { Col, Container, Row, Table, Button as BootstrapButton } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import { useLocation } from "react-router-dom";
import PubSub from "pubsub-js";
import Select from "react-select";
import Helmet from "react-helmet";
import Main from "../layout/Main";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import MountingStructureForm from "./MountingStructureForm";
import { BOM_CATEGORY_OPTIONS, BOM_MAKE_OPTIONS } from "../../constants/bomConstants";

const BomEdit = (props) => {
  const { id } = useParams();
  const [validated, setValidated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [bomType, setBomType] = useState('STANDARD'); // Added By Farhan Khan on 29-Jan-2026
  const [bom, setBom] = useState({ name: "", status: "", bom_type: "STANDARD" });
  const [items, setItems] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [itemErrors, setItemErrors] = useState({});

  const unitOptions = [
    { value: "Nos", label: "Nos" },
    { value: "Set", label: "Set" },
    { value: "Mtr", label: "Mtr" },
    { value: "SqM", label: "SqM" },
    { value: "Kg", label: "Kg" },
    { value: "KWp", label: "KWp" },
    { value: "KWh", label: "KWh" },
    { value: "Box", label: "Box" },
    { value: "Lot", label: "Lot" }
  ];

  useEffect(() => {
    async function init() {
      // Fixed By System on 06-Feb-2026 - Reset items state when ID changes to prevent duplication
      setItems([]);

      await fetchMaterialProducts();
      await fetchActiveBrands();

      if (id) {
        // Editing existing BOM
        await loadBomCategoryWithItems();
      } else {
        // New BOM: set default name (BOM Name field removed from UI)
        setBom(prev => ({ ...prev, name: prev.name || "BOM" }));
      }
    }
    init();
  }, [id]);

  // Added By Farhan Khan on 29-Jan-2026
  // Load BOM category with items and set BOM type
  // Updated by Farhan Khan on 31-Jan-2026 - Parse parts from JSONB
  // Fixed By System on 06-Feb-2026 - Fetch mounting structure items separately for MOUNTING_STRUCTURE BOMs
  const loadBomCategoryWithItems = async () => {
    try {
      const bomData = await solisPowerApi.fetchBomCategoryWithItems(id);
      if (bomData) {
        const bomTypeValue = bomData.bom_type || 'STANDARD';
        setBomType(bomTypeValue);
        setBom({
          id: bomData.id,
          name: bomData.name,
          status: bomData.status,
          bom_type: bomTypeValue,
          // Include mounting structure fields if present
          roof_type: bomData.roof_type || null,
          structure_height_front: bomData.structure_height_front || null,
          structure_height_back: bomData.structure_height_back || null,
          tin_shed_structure_type: bomData.tin_shed_structure_type || null,
          tin_shed_mounting_type: bomData.tin_shed_mounting_type || null,
          cleaning_system_notes: bomData.cleaning_system_notes || null,
          others_notes: bomData.others_notes || null
        });

        // Fixed By System on 06-Feb-2026 - Load items based on BOM type
        // Fixed By System on 06-Feb-2026 - Prevent duplication by only loading from one source
        // Fixed By System on 06-Feb-2026 - Load parts from mounting_structure_parts table
        if (bomTypeValue === 'MOUNTING_STRUCTURE') {
          // For MOUNTING_STRUCTURE BOMs, fetch mounting structure items separately
          try {
            console.log('[BOM Edit] Loading mounting structure items for BOM ID:', id);
            const mountingItems = await solisPowerApi.fetchMountingStructuresByCategory(id);
            console.log('[BOM Edit] Fetched mounting structure items:', mountingItems);

            // Fixed By System on 06-Feb-2026 - Always set items array, even if empty, to prevent duplication
            if (mountingItems && Array.isArray(mountingItems) && mountingItems.length > 0) {
              // Fixed By System on 06-Feb-2026 - Load parts from mounting_structure_parts table for each structure
              const itemsWithParts = await Promise.all(
                mountingItems.map(async (item) => {
                  try {
                    // Fetch parts for this mounting structure
                    const parts = await solisPowerApi.fetchMountingStructurePartsByStructure(item.id);

                    // Convert parts from DB format to frontend format
                    // shape and make are stored as comma-separated strings, convert back to arrays
                    const partsArray = (parts && Array.isArray(parts) ? parts : []).map(part => ({
                      component_name: part.component_name || '',
                      specs: part.specs || '',
                      size: part.size || '',
                      shape: part.shape ? (typeof part.shape === 'string' ? part.shape.split(',').filter(s => s.trim()) : []) : [],
                      make: part.make ? (typeof part.make === 'string' ? part.make.split(',').filter(m => m.trim()) : []) : [],
                      others: part.others || ''
                    }));

                    return {
                      ...item,
                      parts: partsArray
                    };
                  } catch (partError) {
                    console.error('[BOM Edit] Error fetching parts for structure', item.id, ':', partError);
                    return {
                      ...item,
                      parts: []
                    };
                  }
                })
              );

              // Normalize parts structure and brand_id for multi-select
              const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
              const itemsWithParsedParts = itemsWithParts.map(item => {
                if (!Array.isArray(item.parts)) item.parts = [];
                item.parts = normalizeParts(item.parts);
                item.brand_id = toArr(item.brand_id);
                return item;
              });

              console.log('[BOM Edit] Setting mounting structure items with parts:', itemsWithParsedParts.length, 'items');
              // Fixed By System on 06-Feb-2026 - Replace items array completely, don't append
              setItems(itemsWithParsedParts);
            } else {
              console.log('[BOM Edit] No mounting structure items found, setting empty array');
              // Fixed By System on 06-Feb-2026 - Explicitly set empty array to prevent undefined state
              setItems([]);
            }
          } catch (error) {
            console.error('[BOM Edit] Error fetching mounting structure items:', error);
            // Fixed By System on 06-Feb-2026 - Don't use fallback from bomData.items to prevent mixing sources
            // Set empty array instead - mounting structure items should only come from mounting_structures table
            setItems([]);
          }
        } else {
          // For STANDARD BOMs, use items from bom_master_items
          const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
          const itemsWithParsedParts = (bomData.items || []).map(item => {
            if (item.parts) {
              // If parts is a string (JSONB), parse it
              if (typeof item.parts === 'string') {
                try {
                  item.parts = JSON.parse(item.parts);
                } catch (e) {
                  console.warn('Failed to parse parts JSONB:', e);
                  item.parts = [];
                }
              }
              // Ensure parts is an array
              if (!Array.isArray(item.parts)) {
                item.parts = [];
              }
              // Normalize parts structure
              item.parts = normalizeParts(item.parts);
            } else {
              item.parts = [];
            }
            // Normalize brand_id to array for multi-select
            item.brand_id = toArr(item.brand_id);
            return item;
          });

          setItems(itemsWithParsedParts);
        }
      }
    } catch (error) {
      console.error("Error fetching BOM:", error);
    }
  };

  const fetchMaterialProducts = async () => {
    const products = await solisPowerApi.fetchMaterialProducts();
    if (products) {
      const options = products.map(product => ({
        value: product.id,
        label: product.name,
        product: product
      }));
      setProductOptions(options);
    }
  };

  const fetchActiveBrands = async () => {
    const brands = await solisPowerApi.fetchActiveBrands();
    if (brands) {
      const options = brands.map(brand => ({
        value: brand.id,
        label: brand.name
      }));
      setBrandOptions(options);
    }
  };

  const handleBomChange = (e) => {
    const { name, value } = e.target;
    setBom({ ...bom, [name]: value });
  };

  // Bill of Material: only s_no, product_id, specification, category, size_range_capacity, make, unit, quantity, manufacturer_warranty
  // name, description, brand_id, status are derived from product selection for backend compatibility
  // brand_id: array of UUIDs (multi-select)
  const toBrandIdsArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
  // Ensure brand_id is proper array for API (PostgreSQL uuid[]), never stringified
  const toBrandIdPayload = (v) => { const a = toBrandIdsArr(v); return a.length > 0 ? a : null; };
  const addItemRow = () => {
    const newItem = {
      id: `temp_${Date.now()}`,
      s_no: items.length + 1,
      product_id: null,
      product_name: null,
      specification: "",
      category: "", // Multi-select: stored as comma-separated string
      size_range_capacity: "",
      make: "",
      unit: "",
      quantity: 1,
      manufacturer_warranty: "",
      // Backend-required fields (hidden, derived from product)
      name: "",
      description: "",
      brand_id: [],
      status: "Active"
    };
    setItems([...items, newItem]);
  };

  const removeItemRow = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    // Re-sequence S_NO for Bill of Material rows
    if (bomType === 'STANDARD') {
      newItems.forEach((item, i) => { item.s_no = i + 1; });
    }
    setItems(newItems);

    // Clear errors for removed row
    const newErrors = {};
    Object.keys(itemErrors).forEach(key => {
      if (!key.startsWith(`${index}_`)) {
        const parts = key.split('_');
        const rowIndex = parseInt(parts[0]);
        if (rowIndex > index) {
          const field = parts.slice(1).join('_');
          newErrors[`${rowIndex - 1}_${field}`] = itemErrors[key];
        } else {
          newErrors[key] = itemErrors[key];
        }
      }
    });
    setItemErrors(newErrors);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);

    // Clear error for this field
    const errorKey = `${index}_${field}`;
    const newErrors = { ...itemErrors };
    delete newErrors[errorKey];
    setItemErrors(newErrors);
  };

  const handleProductChange = async (index, selectedOption) => {
    const newItems = [...items];
    const item = newItems[index];

    if (selectedOption) {
      item.product_id = selectedOption.value;
      item.product_name = selectedOption.label;
      item.name = selectedOption.label; // For Bill of Material: name derived from product for backend
      item.description = selectedOption.label; // Default; API may override
      // Auto-fill description and brand from product
      try {
        const productDetails = await solisPowerApi.fetchProductDetailsForBom(selectedOption.value);
        if (productDetails) {
          item.description = productDetails.description || item.description || item.product_name;
          item.brand_id = toBrandIdsArr(productDetails.brand_id || item.brand_id);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    } else {
      item.product_id = null;
      item.product_name = null;
      item.name = "";
      item.description = "";
      item.brand_id = [];
    }

    setItems(newItems);
  };

  const handleBrandChange = (index, selected) => {
    const arr = selected && Array.isArray(selected) ? selected : [];
    handleItemChange(index, "brand_id", arr.map(s => s.value));
  };

  const handleUnitChange = (index, selectedOption) => {
    handleItemChange(index, "unit", selectedOption ? selectedOption.value : null);
  };

  // Normalize roof type value - ensures consistent format for validation and API
  // Added By System on 29-Jan-2026 - Fix roof type validation issue
  // Updated by Farhan Khan on 31-Jan-2026 - Enhanced normalization
  const normalizeRoofType = (roofType) => {
    if (!roofType) return null;

    // Handle React Select object
    const rawValue = roofType?.value || roofType;

    // Guard against null/undefined/empty objects
    if (!rawValue || (typeof rawValue === 'object' && Object.keys(rawValue).length === 0)) {
      return null;
    }

    // Convert to string, trim, uppercase, remove spaces
    const normalized = rawValue?.toString().trim().toUpperCase().replace(/\s+/g, '');

    // Map "TINSHED" variations to standard "TINSHED" (already normalized, but ensure consistency)
    if (normalized === 'TINSHED') {
      return 'TINSHED';
    }

    // Return normalized value if valid
    const validTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
    return validTypes.includes(normalized) ? normalized : null;
  };

  // Normalize parts array - ensures clean structure for JSONB storage
  // Updated by Farhan Khan on 31-Jan-2026
  // Fixed by Farhan Khan on 31-Jan-2026 - Handle multi-select arrays for shape and make
  const normalizeParts = (parts) => {
    // Ensure parts is always an array
    if (!parts) return [];
    if (!Array.isArray(parts)) return [];

    // Filter out null/undefined entries and normalize each part
    return parts
      .filter(part => part !== null && part !== undefined)
      .map(part => {
        // Normalize shape - ensure it's an array
        let normalizedShape = [];
        if (Array.isArray(part.shape)) {
          normalizedShape = part.shape.filter(s => s); // Remove empty values
        } else if (part.shape && typeof part.shape === 'string') {
          // Handle legacy string format
          normalizedShape = part.shape.split(',').map(s => s.trim()).filter(s => s);
        }

        // Normalize make - ensure it's an array
        let normalizedMake = [];
        if (Array.isArray(part.make)) {
          normalizedMake = part.make.filter(m => m); // Remove empty values
        } else if (part.make && typeof part.make === 'string') {
          // Handle legacy string format
          normalizedMake = part.make.split(',').map(m => m.trim()).filter(m => m);
        }

        return {
          component_name: (part.component_name || part.part_name || "").trim(), // Trim component_name
          specs: part.specs || "",
          size: part.size || "",
          shape: normalizedShape, // Array for multi-select
          make: normalizedMake,   // Array for multi-select
          others: part.others || ""
        };
      })
      .filter(part => part.component_name && part.component_name.trim()); // Remove parts without component_name after trimming
  };

  const validateBom = () => {
    if (!bom.name || !bom.name.trim()) {
      return "BOM name is required";
    }
    if (!bom.status) {
      return "Status is required";
    }

    // Added By Farhan Khan on 29-Jan-2026 - Validate mounting structure fields
    // Updated By System on 29-Jan-2026 - Normalize roof_type before validation
    if (bomType === 'MOUNTING_STRUCTURE') {
      const validRoofTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
      const normalizedRoofType = normalizeRoofType(bom.roof_type);

      // if (!normalizedRoofType || !validRoofTypes.includes(normalizedRoofType)) {
      //   return "Valid roof type is required for mounting structure (RCC, GROUND, TINSHED, or COMBO)";
      // }

      // if (normalizedRoofType === 'TINSHED') {
      //   if (!bom.tin_shed_structure_type || !bom.tin_shed_structure_type.trim()) {
      //     return "Structure type is required for Tin Shed roof type";
      //   }
      //   if (!bom.tin_shed_mounting_type || !bom.tin_shed_mounting_type.trim()) {
      //     return "Mounting type is required for Tin Shed roof type";
      //   }
      // }
    }

    return null;
  };

  const validateItems = () => {
    const errors = {};
    const toastMessages = [];

    // Bill of Material validation - Product, Unit, Brand required
    if (bomType === 'STANDARD') {
      items.forEach((item, index) => {
        if (!item.product_id) {
          errors[`${index}_product_id`] = "Product is required";
          toastMessages.push(`Please select Product for Item #${index + 1}`);
        }
        if (!item.unit || !item.unit.trim()) {
          errors[`${index}_unit`] = "Unit is required";
          toastMessages.push(`Please fill Unit for Item #${index + 1}`);
        }
        if (!toBrandIdsArr(item.brand_id).length) {
          errors[`${index}_brand_id`] = "Brand is required";
          toastMessages.push(`Please select Brand for Item #${index + 1}`);
        }
      });
    }

    // MOUNTING STRUCTURE BOM validation
    // Updated By System on 29-Jan-2026 - Add roof_type validation for line items
    if (bomType === 'MOUNTING_STRUCTURE') {
      items.forEach((item, index) => {
        // Validate roof_type for line items (required field)
        const normalizedRoofType = normalizeRoofType(item.roof_type);
        const validRoofTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
        if (!normalizedRoofType || !validRoofTypes.includes(normalizedRoofType)) {
          errors[`${index}_roof_type`] = "Valid roof type is required";
          toastMessages.push(`Please select a valid Roof Type for Line Item #${index + 1}`);
        }

        // Validate structure heights (required fields)
        if (!item.structure_height_front || item.structure_height_front === null || item.structure_height_front === '') {
          errors[`${index}_structure_height_front`] = "Structure height front is required";
          toastMessages.push(`Please fill Structure Height Front for Line Item #${index + 1}`);
        }
        if (!item.structure_height_back || item.structure_height_back === null || item.structure_height_back === '') {
          errors[`${index}_structure_height_back`] = "Structure height back is required";
          toastMessages.push(`Please fill Structure Height Back for Line Item #${index + 1}`);
        }

        // Validate COMPONENT items
        // Fixed by Farhan Khan on 31-Jan-2026 - Check for trimmed empty strings, not just falsy values
        // if (item.item_type === 'COMPONENT') {
        //   const componentName = item.component_name ? item.component_name.toString().trim() : '';
        //   if (!componentName || componentName === '') {
        //     errors[`${index}_component_name`] = "Component name is required";
        //     toastMessages.push(`Please fill Component Name for Item #${index + 1}`);
        //   }
        //   // For non-OTHERS components, validate specs and size
        //   if (item.component_name && item.component_name !== 'OTHERS') {
        //     const specsArray = typeof item.specs === 'string' 
        //       ? (item.specs === '[]' ? [] : (item.specs ? JSON.parse(item.specs) : []))
        //       : (item.specs || []);
        //     if (!specsArray || specsArray.length === 0) {
        //       errors[`${index}_specs`] = "Specs is required";
        //       toastMessages.push(`Please fill Specs for Item #${index + 1}`);
        //     }
        //     if (!item.size || !item.size.trim()) {
        //       errors[`${index}_size`] = "Size is required";
        //       toastMessages.push(`Please fill Size for Item #${index + 1}`);
        //     }
        //   }
        // }

        // Validate COMBO_DISTRIBUTION items
        if (item.item_type === 'COMBO_DISTRIBUTION') {
          if (item.number_of_panels === undefined || item.number_of_panels === null || item.number_of_panels === '') {
            errors[`${index}_number_of_panels`] = "Number of panels is required";
            toastMessages.push(`Please fill Number of Panels for Item #${index + 1}`);
          }
        }

        // Validate SPECIAL_STRUCTURE items
        if (item.item_type === 'SPECIAL_STRUCTURE') {
          if (!item.special_structure_type) {
            errors[`${index}_special_structure_type`] = "Structure type is required";
            toastMessages.push(`Please fill Structure Type for Item #${index + 1}`);
          }
        }

        // Common validations for all Mounting Structure items
        if (!item.status) {
          errors[`${index}_status`] = "Status is required";
          toastMessages.push(`Please fill Status for Item #${index + 1}`);
        }
      });
    }

    setItemErrors(errors);

    // Show toast notification with error messages
    if (Object.keys(errors).length > 0) {
      // Show first error message (most user-friendly)
      // If multiple errors exist, user can see all inline errors in the form
      const errorMessage = toastMessages.length > 0
        ? toastMessages[0]
        : "Please fill all required fields";
      return { isValid: false, message: errorMessage };
    }

    return { isValid: true, message: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    // Validate BOM header
    const bomError = validateBom();
    if (bomError) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Validation Error",
        message: bomError,
      });
      return;
    }

    // Fixed By System on 06-Feb-2026 - Separate STANDARD and MOUNTING_STRUCTURE fields
    // Fixed By System on 06-Feb-2026 - Include id for update operations
    // STANDARD BOM: Only name, status, bom_type
    // MOUNTING_STRUCTURE BOM: name, status, bom_type, and mounting structure fields only
    let bomToSave = {
      name: bom.name,
      status: bom.status,
      bom_type: bomType
    };

    // Fixed By System on 06-Feb-2026 - Include id for update operations
    if (bom.id) {
      bomToSave.id = bom.id;
    }

    // Add MOUNTING_STRUCTURE specific fields ONLY if bom_type is MOUNTING_STRUCTURE
    if (bomType === 'MOUNTING_STRUCTURE') {
      // Fixed By System on 06-Feb-2026 - Add logging and ensure roof_type is passed correctly
      console.log('[BOM Edit] MOUNTING_STRUCTURE BOM - bom.roof_type:', bom.roof_type, 'bomType:', bomType);

      // Normalize roof_type if present
      // Note: roof_type is optional on header, primarily stored on line items
      if (bom.roof_type) {
        const normalizedRoofType = normalizeRoofType(bom.roof_type);
        console.log('[BOM Edit] Normalized roof_type:', normalizedRoofType, 'from:', bom.roof_type);
        if (normalizedRoofType) {
          bomToSave.roof_type = normalizedRoofType;
        }
      } else {
        console.log('[BOM Edit] No roof_type on BOM header (this is OK - roof_type is on line items)');
      }
      // Add other mounting structure header fields
      if (bom.structure_height_front !== undefined && bom.structure_height_front !== null) {
        bomToSave.structure_height_front = bom.structure_height_front;
      }
      if (bom.structure_height_back !== undefined && bom.structure_height_back !== null) {
        bomToSave.structure_height_back = bom.structure_height_back;
      }
      if (bom.tin_shed_structure_type) {
        bomToSave.tin_shed_structure_type = bom.tin_shed_structure_type;
      }
      if (bom.tin_shed_mounting_type) {
        bomToSave.tin_shed_mounting_type = bom.tin_shed_mounting_type;
      }
      if (bom.cleaning_system_notes) {
        bomToSave.cleaning_system_notes = bom.cleaning_system_notes;
      }
      if (bom.others_notes) {
        bomToSave.others_notes = bom.others_notes;
      }
    }
    // STANDARD BOM: Do NOT include any mounting structure fields

    let bomResult;
    if (bom.id) {
      // Fixed By System on 06-Feb-2026 - Ensure id is included in payload
      bomResult = await solisPowerApi.saveBomCategory(bomToSave);
    } else {
      bomResult = await solisPowerApi.createBomCategory(bomToSave);
    }

    if (!bomResult || !bomResult.success) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Failed to save BOM header",
      });
      return;
    }

    const savedBomId = bomResult.id || bom.id;

    // Step 2: Validate items (for both STANDARD and MOUNTING_STRUCTURE BOMs)
    if (items.length > 0) {
      const validationResult = validateItems();
      if (!validationResult.isValid) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Validation Error",
          message: validationResult.message || "Please fill all required fields",
        });
        return;
      }
    }

    // Step 3: Delete existing items and save new items
    // if (bom.id) {
    //   // Delete existing items for this BOM
    //   const existingItems = items.filter(item => item.id && !item.id.toString().startsWith('temp_'));
    //   for (const item of existingItems) {
    //     await solisPowerApi.deleteBomMasterItem(item.id);
    //   }
    // }

    // Step 4: Save all items
    // Fixed By System on 06-Feb-2026 - Separate STANDARD and MOUNTING_STRUCTURE item saving
    // Fixed By System on 06-Feb-2026 - Filter out invalid/empty items before building payload
    if (items.length > 0) {
      const standardItemsToSave = [];
      const mountingStructureItemsToSave = [];

      // Fixed By System on 06-Feb-2026 - Filter out invalid/empty items before building payload
      // Only process items that have at least some valid data
      const validItems = items.filter(item => {
        // Skip null/undefined items
        if (!item) return false;

        // For MOUNTING_STRUCTURE, item must have at least:
        // - roof_type OR
        // - structure_height_front/back OR
        // - special_structure_type OR
        // - combo_panels fields OR
        // - parts array with valid parts
        if (bomType === 'MOUNTING_STRUCTURE') {
          const hasRoofType = item.roof_type && (typeof item.roof_type === 'string' ? item.roof_type.trim() !== '' : item.roof_type);
          const hasStructureHeight = item.structure_height_front || item.structure_height_back;
          const hasSpecialStructure = item.special_structure_type && item.special_structure_type.trim && item.special_structure_type.trim() !== '';
          const hasComboPanels = item.combo_panels_rcc || item.combo_panels_ground || item.combo_panels_tinshed;
          const hasValidParts = item.parts && Array.isArray(item.parts) && item.parts.length > 0 &&
            item.parts.some(part => part && part.component_name && part.component_name.trim && part.component_name.trim());

          return hasRoofType || hasStructureHeight || hasSpecialStructure || hasComboPanels || hasValidParts;
        }
        // For STANDARD, item must have product_id or name
        return item.product_id || (item.name && item.name.trim && item.name.trim()) || (item.component_name && item.component_name.trim && item.component_name.trim());
      });

      console.log('[BOM Edit] Filtered items:', items.length, '->', validItems.length, 'valid items');
      if (items.length !== validItems.length) {
        console.log('[BOM Edit] Removed', items.length - validItems.length, 'invalid/empty items from payload');
      }

      validItems.forEach(item => {
        // Base fields common to all item types
        // For Bill of Material (STANDARD): name/description derived from product
        const baseItem = {
          name: item.name || item.product_name || item.component_name || item.item_name || "BOM Item",
          description: item.description || item.product_name || item.component_name || item.item_name || item.name || "BOM Item",
          unit: item.unit || "Nos",
          status: item.status || "Active",
          bom_category_id: savedBomId // Fixed: Use bom_category_id (not bom_categories_id)
        };

        // Check if item is Mounting Structure type (by bomType or item.item_type)
        const isMountingStructure = bomType === 'MOUNTING_STRUCTURE' ||
          item.item_type === 'COMPONENT' ||
          item.item_type === 'COMBO_DISTRIBUTION' ||
          item.item_type === 'SPECIAL_STRUCTURE' ||
          item.roof_type || // New: check for roof_type field
          (item.parts && item.parts.length > 0); // New: check for parts array

        // STANDARD BOM items - ONLY STANDARD fields, NO mounting structure fields
        if (!isMountingStructure && (bomType === 'STANDARD' || item.item_type === 'STANDARD' || !item.item_type)) {
          standardItemsToSave.push({
            s_no: item.s_no ?? (standardItemsToSave.length + 1),
            name: baseItem.name,
            specification: item.specification || null,
            category: item.category || null,
            size_range_capacity: item.size_range_capacity || null,
            make: item.make || null,
            unit: baseItem.unit,
            quantity: item.quantity ?? 1,
            manufacturer_warranty: item.manufacturer_warranty || null,
            description: baseItem.description,
            status: baseItem.status,
            bom_categories_id: baseItem.bom_category_id, // bom_master_items uses bom_categories_id
            item_type: 'STANDARD',
            product_id: item.product_id || null,
            brand_id: toBrandIdPayload(item.brand_id),
            // DO NOT include: roof_type, structure_height_front, structure_height_back, 
            // component_name, specs, shape, parts, or any mounting structure fields
          });
          return;
        }

        // MOUNTING STRUCTURE items - Handle new line item structure with parts
        // Updated By System on 29-Jan-2026 - Normalize roof_type before saving
        // Updated by Farhan Khan on 31-Jan-2026 - Store parts as JSONB array instead of flattening
        if (isMountingStructure) {
          // Normalize roof_type for this item
          const normalizedRoofType = normalizeRoofType(item.roof_type);

          // Normalize parts array for JSONB storage
          // Updated by Farhan Khan on 31-Jan-2026
          const normalizedParts = normalizeParts(item.parts);

          // MOUNTING STRUCTURE COMPONENT items - ONLY mounting structure fields
          // Fixed By System on 06-Feb-2026 - Explicit field mapping for COMPONENT items
          // Fixed By System on 06-Feb-2026 - Include parts array so they can be saved separately later
          if (normalizedParts && Array.isArray(normalizedParts) && normalizedParts.length > 0) {
            // Extract component_name from first part that has component_name for COMPONENT item validation
            const partWithComponentName = normalizedParts.find(part => part.component_name && part.component_name.trim());
            const firstPartComponentName = partWithComponentName?.component_name?.trim() || null;
            const lineItemWithParts = {
              s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
              name: baseItem.name,
              description: baseItem.description,
              unit: baseItem.unit,
              status: baseItem.status,
              bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
              item_type: 'COMPONENT',
              brand_id: toBrandIdPayload(item.brand_id), // Optional for mounting structure
              roof_type: normalizedRoofType || null,
              structure_height_front: item.structure_height_front || null,
              structure_height_back: item.structure_height_back || null,
              component_name: firstPartComponentName,
              parts: normalizedParts, // Fixed By System on 06-Feb-2026 - Include parts array for later extraction
              // Parts will be stored separately in mounting_structure_parts table
              tin_shed_structure_type: item.tin_shed_structure_type || null,
              tin_shed_mounting_type: item.tin_shed_mounting_type || null,
              combo_panels_rcc: item.combo_panels_rcc || null,
              combo_panels_ground: item.combo_panels_ground || null,
              combo_panels_tinshed: item.combo_panels_tinshed || null,
              combo_roof_type: normalizedRoofType || null,
              // DO NOT include: product_id (STANDARD field only)
            };
            mountingStructureItemsToSave.push(lineItemWithParts);
          } else if (normalizedRoofType && (item.structure_height_front || item.structure_height_back)) {
            // Save line item even without parts (for structure-only items)
            // Fixed By System on 06-Feb-2026 - Include parts array (even if empty) for consistency
            const lineItemWithoutParts = {
              s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
              name: baseItem.name,
              description: baseItem.description,
              unit: baseItem.unit,
              status: baseItem.status,
              bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
              item_type: 'COMPONENT',
              brand_id: toBrandIdPayload(item.brand_id),
              roof_type: normalizedRoofType || null,
              structure_height_front: item.structure_height_front || null,
              structure_height_back: item.structure_height_back || null,
              component_name: item.component_name?.trim() || null,
              parts: normalizedParts || [], // Fixed By System on 06-Feb-2026 - Include parts array (empty if no parts)
              // Parts will be stored separately in mounting_structure_parts table
              tin_shed_structure_type: item.tin_shed_structure_type || null,
              tin_shed_mounting_type: item.tin_shed_mounting_type || null,
              combo_panels_rcc: item.combo_panels_rcc || null,
              combo_panels_ground: item.combo_panels_ground || null,
              combo_panels_tinshed: item.combo_panels_tinshed || null,
              combo_roof_type: normalizedRoofType || null,
              // DO NOT include: product_id (STANDARD field only)
            };
            mountingStructureItemsToSave.push(lineItemWithoutParts);
          }

          // MOUNTING STRUCTURE SPECIAL_STRUCTURE items - ONLY mounting structure fields
          // Fixed By System on 06-Feb-2026 - Explicit field mapping for SPECIAL_STRUCTURE items
          // Fixed By System on 06-Feb-2026 - Include parts array so they can be saved separately later
          if (item.special_structure_type) {
            const normalizedSpecialParts = normalizeParts(item.parts);
            const specialItem = {
              s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
              name: baseItem.name,
              description: baseItem.description,
              unit: baseItem.unit,
              status: baseItem.status,
              bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
              item_type: 'SPECIAL_STRUCTURE',
              brand_id: toBrandIdPayload(item.brand_id),
              roof_type: normalizedRoofType || null,
              structure_height_front: item.structure_height_front || null,
              structure_height_back: item.structure_height_back || null,
              special_structure_type: item.special_structure_type,
              specs: item.special_structure_specs || '',
              length: item.special_structure_length !== undefined && item.special_structure_length !== null
                ? parseFloat(item.special_structure_length) : null,
              notes: item.special_structure_type === 'CLEANING SYSTEM'
                ? item.cleaning_system
                : (item.special_structure_type === 'OTHERS' ? item.others_text : null),
              parts: normalizedSpecialParts, // Fixed By System on 06-Feb-2026 - Include parts array for later extraction
              // Parts will be stored separately in mounting_structure_parts table
              // DO NOT include: product_id, component_name (COMPONENT fields only)
            };
            mountingStructureItemsToSave.push(specialItem);
          }

          // MOUNTING STRUCTURE COMBO_DISTRIBUTION items - ONLY mounting structure fields
          // Fixed By System on 06-Feb-2026 - Explicit field mapping for COMBO_DISTRIBUTION items
          // Fixed By System on 06-Feb-2026 - Include parts array so they can be saved separately later
          if (normalizedRoofType === 'COMBO' && (item.combo_panels_rcc || item.combo_panels_ground || item.combo_panels_tinshed)) {
            const normalizedComboParts = normalizeParts(item.parts);
            const comboItem = {
              s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
              name: baseItem.name,
              description: baseItem.description,
              unit: baseItem.unit,
              status: baseItem.status,
              bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
              item_type: 'COMBO_DISTRIBUTION',
              brand_id: toBrandIdPayload(item.brand_id),
              combo_roof_type: normalizedRoofType,
              structure_height_front: item.structure_height_front || null,
              structure_height_back: item.structure_height_back || null,
              number_of_panels: (item.combo_panels_rcc || 0) + (item.combo_panels_ground || 0) + (item.combo_panels_tinshed || 0),
              combo_panels_rcc: item.combo_panels_rcc || null,
              combo_panels_ground: item.combo_panels_ground || null,
              combo_panels_tinshed: item.combo_panels_tinshed || null,
              parts: normalizedComboParts, // Fixed By System on 06-Feb-2026 - Include parts array for later extraction
              // Parts will be stored separately in mounting_structure_parts table
              // DO NOT include: product_id, component_name (STANDARD/COMPONENT fields only)
            };
            mountingStructureItemsToSave.push(comboItem);
          }

          // Legacy structure handling (if no parts array, use old structure)
          // Updated by Farhan Khan on 31-Jan-2026 - Store parts as empty array for backward compatibility
          if (!normalizedParts || !Array.isArray(normalizedParts) || normalizedParts.length === 0) {
            // Check if this is a legacy item (has component_name but no parts array)
            if (item.component_name && !item.parts) {
              const mountingItem = {
                ...baseItem,
                item_type: item.item_type || 'COMPONENT',
                brand_id: toBrandIdPayload(item.brand_id),
                roof_type: normalizedRoofType || null,
                structure_height_front: item.structure_height_front || null,
                structure_height_back: item.structure_height_back || null,
                parts: [], // Empty parts array for backward compatibility
              };

              // MOUNTING STRUCTURE COMPONENT items (LEGS, RAFTER, PURLIN, etc.) - Legacy format
              // Fixed By System on 06-Feb-2026 - Explicit field mapping, NO STANDARD fields
              if (item.item_type === 'COMPONENT') {
                const normalizedComboRoofType = normalizeRoofType(item.combo_roof_type);

                // Normalize specs, shape, make to arrays if they're strings
                let normalizedSpecs = item.specs;
                if (typeof normalizedSpecs === 'string') {
                  try {
                    const parsed = JSON.parse(normalizedSpecs);
                    normalizedSpecs = Array.isArray(parsed) ? parsed : (normalizedSpecs ? [normalizedSpecs] : []);
                  } catch {
                    normalizedSpecs = normalizedSpecs ? [normalizedSpecs] : [];
                  }
                } else if (!Array.isArray(normalizedSpecs)) {
                  normalizedSpecs = normalizedSpecs ? [normalizedSpecs] : [];
                }

                let normalizedShape = item.shape;
                if (typeof normalizedShape === 'string') {
                  try {
                    const parsed = JSON.parse(normalizedShape);
                    normalizedShape = Array.isArray(parsed) ? parsed : (normalizedShape ? [normalizedShape] : []);
                  } catch {
                    normalizedShape = normalizedShape ? [normalizedShape] : [];
                  }
                } else if (!Array.isArray(normalizedShape)) {
                  normalizedShape = normalizedShape ? [normalizedShape] : [];
                }

                let normalizedMake = item.make;
                if (typeof normalizedMake === 'string') {
                  try {
                    const parsed = JSON.parse(normalizedMake);
                    normalizedMake = Array.isArray(parsed) ? parsed : (normalizedMake ? [normalizedMake] : []);
                  } catch {
                    normalizedMake = normalizedMake ? [normalizedMake] : [];
                  }
                } else if (!Array.isArray(normalizedMake)) {
                  normalizedMake = normalizedMake ? [normalizedMake] : [];
                }

                mountingStructureItemsToSave.push({
                  s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
                  name: mountingItem.name,
                  description: mountingItem.description,
                  unit: mountingItem.unit,
                  status: mountingItem.status,
                  bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
                  item_type: 'COMPONENT',
                  brand_id: toBrandIdPayload(mountingItem.brand_id),
                  roof_type: mountingItem.roof_type || null,
                  structure_height_front: mountingItem.structure_height_front || null,
                  structure_height_back: mountingItem.structure_height_back || null,
                  component_name: (item.component_name || item.name || "").trim(),
                  // Specs, size, shape, make will be stored in mounting_structure_parts table
                  combo_roof_type: normalizedComboRoofType || null,
                  // DO NOT include: product_id (STANDARD field only)
                });
                return;
              }

              // MOUNTING STRUCTURE COMBO_DISTRIBUTION items - Legacy format
              // Fixed By System on 06-Feb-2026 - Explicit field mapping, NO STANDARD fields
              if (item.item_type === 'COMBO_DISTRIBUTION') {
                const normalizedComboRoofType = normalizeRoofType(item.combo_roof_type);
                mountingStructureItemsToSave.push({
                  s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
                  name: mountingItem.name,
                  description: mountingItem.description,
                  unit: mountingItem.unit,
                  status: mountingItem.status,
                  bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
                  item_type: 'COMBO_DISTRIBUTION',
                  brand_id: toBrandIdPayload(mountingItem.brand_id),
                  roof_type: mountingItem.roof_type || null,
                  structure_height_front: mountingItem.structure_height_front || null,
                  structure_height_back: mountingItem.structure_height_back || null,
                  combo_roof_type: normalizedComboRoofType,
                  number_of_panels: item.number_of_panels !== undefined && item.number_of_panels !== null
                    ? parseInt(item.number_of_panels) || 0
                    : 0,
                  combo_panels_rcc: item.combo_panels_rcc || null,
                  combo_panels_ground: item.combo_panels_ground || null,
                  combo_panels_tinshed: item.combo_panels_tinshed || null,
                  tin_shed_structure_type: item.tin_shed_structure_type || null,
                  tin_shed_mounting_type: item.tin_shed_mounting_type || null,
                  // Parts will be stored separately in mounting_structure_parts table
                  // DO NOT include: product_id, component_name (STANDARD/COMPONENT fields only)
                });
                return;
              }

              // MOUNTING STRUCTURE SPECIAL_STRUCTURE items - Legacy format
              // Fixed By System on 06-Feb-2026 - Explicit field mapping, NO STANDARD fields
              if (item.item_type === 'SPECIAL_STRUCTURE') {
                mountingStructureItemsToSave.push({
                  s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
                  name: mountingItem.name,
                  description: mountingItem.description,
                  unit: mountingItem.unit,
                  status: mountingItem.status,
                  bom_category_id: baseItem.bom_category_id, // Fixed: Use bom_category_id for mounting_structure
                  item_type: 'SPECIAL_STRUCTURE',
                  brand_id: toBrandIdPayload(mountingItem.brand_id),
                  roof_type: mountingItem.roof_type || null,
                  structure_height_front: mountingItem.structure_height_front || null,
                  structure_height_back: mountingItem.structure_height_back || null,
                  special_structure_type: item.special_structure_type,
                  specs: item.specs || '',
                  length: item.length !== undefined && item.length !== null ? parseFloat(item.length) : null,
                  notes: item.notes || null,
                  // Parts will be stored separately in mounting_structure_parts table
                  // DO NOT include: product_id, component_name (STANDARD/COMPONENT fields only)
                });
                return;
              }

              // Fallback for unknown Mounting Structure item types
              mountingStructureItemsToSave.push({
                ...mountingItem,
                s_no: item.s_no ?? (mountingStructureItemsToSave.length + 1),
                bom_category_id: baseItem.bom_category_id
              });
            }
          }

          return;
        }

        // Fallback for other BOM types - should not happen, but handle gracefully
        // Fixed by Farhan Khan on 31-Jan-2026 - Ensure component_name is trimmed for all items
        if (baseItem.component_name) {
          baseItem.component_name = baseItem.component_name.trim();
        }
        // If we reach here, it's likely a STANDARD item that wasn't caught earlier
        standardItemsToSave.push(baseItem);
      });

      // Fixed By System on 06-Feb-2026 - Separate STANDARD and MOUNTING_STRUCTURE item saving
      // Save STANDARD items to bom_master_items API - in S_NO sequence
      if (standardItemsToSave.length > 0) {
        const sortedStandardItems = [...standardItemsToSave].sort((a, b) =>
          (a.s_no ?? 9999) - (b.s_no ?? 9999)
        );
        const standardItemsResult = await solisPowerApi.bulkCreateBomMasterItems(sortedStandardItems);

        if (!standardItemsResult || standardItemsResult.errors) {
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error",
            message: standardItemsResult?.errors || "Failed to save STANDARD items",
          });
          return;
        }
      }

      // Save MOUNTING_STRUCTURE items to mounting_structures API
      // Fixed By System on 06-Feb-2026 - Use mounting_structure API, remove parts (stored separately)
      // Fixed By System on 06-Feb-2026 - Delete existing items before creating new ones to prevent duplication
      // Fixed By System on 06-Feb-2026 - Save parts to mounting_structure_parts table after creating structures
      // Fixed By System on 06-Feb-2026 - Always delete existing items on update, even if no new items to save
      if (bom.id) {
        // If updating existing BOM, ALWAYS delete all existing mounting structure items first (this will cascade delete parts)
        // This ensures removed sections are actually removed from DB
        try {
          console.log('[BOM Edit] Deleting existing mounting structure items for BOM ID:', savedBomId);
          const deleteResult = await solisPowerApi.deleteMountingStructuresByCategory(savedBomId);
          console.log('[BOM Edit] Delete result:', deleteResult);
          console.log('[BOM Edit] Successfully deleted existing mounting structure items');
        } catch (error) {
          console.error('[BOM Edit] Error deleting existing mounting structure items:', error);
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error",
            message: "Failed to delete existing items: " + (error.message || "Unknown error"),
          });
          return; // Don't proceed if deletion fails
        }
      }

      // Only create new items if there are any to save
      if (mountingStructureItemsToSave.length > 0) {
        // Sort by s_no and re-sequence before save
        const sortedMountingItems = [...mountingStructureItemsToSave]
          .sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999))
          .map((item, i) => ({ ...item, s_no: i + 1 }));
        mountingStructureItemsToSave.length = 0;
        mountingStructureItemsToSave.push(...sortedMountingItems);

        // Store parts separately before removing from line items
        const partsToSave = []; // Array to collect all parts with their structure IDs
        const itemsWithPartsIndex = []; // Track which items have parts

        mountingStructureItemsToSave.forEach((item, index) => {
          const normalizedParts = normalizeParts(item.parts);
          if (normalizedParts && Array.isArray(normalizedParts) && normalizedParts.length > 0) {
            itemsWithPartsIndex.push(index);
          }
        });

        // Remove parts from line items - parts will be stored separately in mounting_structure_parts table
        const mountingItemsWithoutParts = mountingStructureItemsToSave.map((item, index) => {
          const { parts, ...itemWithoutParts } = item;
          return itemWithoutParts;
        });

        console.log('[BOM Edit] Creating new mounting structure items:', mountingItemsWithoutParts.length);
        console.log('[BOM Edit] Items payload:', JSON.stringify(mountingItemsWithoutParts, null, 2));
        const mountingItemsResult = await solisPowerApi.bulkCreateMountingStructures(mountingItemsWithoutParts);

        if (!mountingItemsResult || mountingItemsResult.errors) {
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error",
            message: mountingItemsResult?.errors || "Failed to save MOUNTING_STRUCTURE items",
          });
          return;
        }
        console.log('[BOM Edit] Successfully created mounting structure items');

        // Fixed By System on 06-Feb-2026 - Save parts to mounting_structure_parts table
        // Map created structure IDs to their parts
        // Fixed By System on 06-Feb-2026 - Handle different response formats
        let createdStructures = [];
        if (mountingItemsResult && mountingItemsResult.data) {
          // Response format: { success: true, data: [...] }
          createdStructures = Array.isArray(mountingItemsResult.data) ? mountingItemsResult.data : [];
        } else if (Array.isArray(mountingItemsResult)) {
          // Response format: [...] (direct array)
          createdStructures = mountingItemsResult;
        }

        console.log('[BOM Edit] Created structures count:', createdStructures.length);
        console.log('[BOM Edit] Created structures:', createdStructures);

        if (createdStructures.length > 0) {
          // Ensure we have the same number of structures as items to save
          if (createdStructures.length !== mountingStructureItemsToSave.length) {
            console.warn('[BOM Edit] Mismatch: Created structures count (' + createdStructures.length + ') != Items to save count (' + mountingStructureItemsToSave.length + ')');
          }

          for (let i = 0; i < createdStructures.length && i < mountingStructureItemsToSave.length; i++) {
            const structureId = createdStructures[i]?.id;
            if (!structureId) {
              console.error('[BOM Edit] Structure at index', i, 'has no ID:', createdStructures[i]);
              continue;
            }

            const originalItem = mountingStructureItemsToSave[i];
            console.log('[BOM Edit] Processing parts for structure', structureId, 'from item:', originalItem);

            // Normalize parts before processing
            const normalizedParts = normalizeParts(originalItem.parts);
            console.log('[BOM Edit] Normalized parts for structure', structureId, ':', normalizedParts);

            if (normalizedParts && Array.isArray(normalizedParts) && normalizedParts.length > 0) {
              // Convert parts to mounting_structure_parts format
              const partsForStructure = normalizedParts.map(part => {
                // Convert shape and make arrays to comma-separated strings
                const shapeStr = Array.isArray(part.shape)
                  ? part.shape.filter(s => s && s.trim()).join(',')
                  : (part.shape && typeof part.shape === 'string' ? part.shape.trim() : '');
                const makeStr = Array.isArray(part.make)
                  ? part.make.filter(m => m && m.trim()).join(',')
                  : (part.make && typeof part.make === 'string' ? part.make.trim() : '');

                const partRecord = {
                  mounting_structure_id: structureId,
                  component_name: (part.component_name || '').trim(),
                  specs: (part.specs || '').trim(),
                  size: (part.size || '').trim(),
                  shape: shapeStr,
                  make: makeStr,
                  others: (part.others || '').trim()
                };

                console.log('[BOM Edit] Prepared part record:', partRecord);
                return partRecord;
              }).filter(part => part.component_name && part.component_name.length > 0); // Only save parts with component_name

              console.log('[BOM Edit] Parts for structure', structureId, 'after filtering:', partsForStructure.length, 'parts');

              if (partsForStructure.length > 0) {
                partsToSave.push(...partsForStructure);
              } else {
                console.log('[BOM Edit] No valid parts to save for structure', structureId, '(all parts filtered out)');
              }
            } else {
              console.log('[BOM Edit] No parts found for structure', structureId, 'normalizedParts:', normalizedParts);
            }
          }

          // Bulk create all parts
          if (partsToSave.length > 0) {
            console.log('[BOM Edit] Saving', partsToSave.length, 'parts to mounting_structure_parts table');
            console.log('[BOM Edit] Parts payload:', JSON.stringify(partsToSave, null, 2));

            try {
              const partsResult = await solisPowerApi.bulkCreateMountingStructureParts(partsToSave);
              console.log('[BOM Edit] Parts save result:', partsResult);

              if (!partsResult) {
                console.error('[BOM Edit] Parts save returned null/undefined');
                PubSub.publish("RECORD_ERROR_TOAST", {
                  title: "Warning",
                  message: "Mounting structures saved but parts save returned no response",
                });
              } else if (partsResult.errors) {
                console.error('[BOM Edit] Error saving parts:', partsResult.errors);
                PubSub.publish("RECORD_ERROR_TOAST", {
                  title: "Warning",
                  message: "Mounting structures saved but parts failed to save: " + (Array.isArray(partsResult.errors) ? partsResult.errors.join(', ') : partsResult.errors),
                });
              } else if (partsResult.success === false) {
                console.error('[BOM Edit] Parts save failed:', partsResult.message);
                PubSub.publish("RECORD_ERROR_TOAST", {
                  title: "Warning",
                  message: "Mounting structures saved but parts failed to save: " + (partsResult.message || "Unknown error"),
                });
              } else {
                console.log('[BOM Edit] Successfully saved', partsToSave.length, 'parts');
                const savedPartsCount = partsResult.data ? (Array.isArray(partsResult.data) ? partsResult.data.length : 1) : partsToSave.length;
                console.log('[BOM Edit] Confirmed saved parts count:', savedPartsCount);
              }
            } catch (partsError) {
              console.error('[BOM Edit] Exception saving parts:', partsError);
              PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Warning",
                message: "Mounting structures saved but exception occurred while saving parts: " + (partsError.message || "Unknown error"),
              });
            }
          } else {
            console.log('[BOM Edit] No parts to save (partsToSave is empty)');
          }
        } else {
          console.warn('[BOM Edit] No created structures found in response, cannot save parts');
        }
      } else if (bom.id) {
        // If updating and no items to save, items were intentionally removed
        // Items have already been deleted above, so this is correct
        console.log('[BOM Edit] No mounting structure items to save - all items removed');
      }
    }

    PubSub.publish("RECORD_SAVED_TOAST", {
      title: "Record Saved",
      message: "BOM saved successfully",
    });

    // Redirect to detail page
    navigate(`/boms/${savedBomId}`, { state: { id: savedBomId } });
  };

  const handleCancel = () => {
    navigate(`/boms`);
  };

  return (
    <Main>
      <>
        <Helmet>
          <title>BOM</title>
        </Helmet>
      </>
      <Container className="view-form">
        <Row>
          <Col></Col>
          <Col lg={10} className="pb-1 pt-2">
            <Link className="nav-link" to="/boms">
              Home <i className="fa-solid fa-chevron-right"></i>{" "}
              <div style={{ color: "#23468c", display: "inline" }}>
                BOM
              </div>
            </Link>
          </Col>
          <Col></Col>
        </Row>
        <Row>
          <Col></Col>
          <Col lg={10}>
            <Form
              className="mt-3"
              onSubmit={handleSubmit}
              noValidate
              validated={validated}
            >
              {/* BOM Header Section */}
              <Row className="view-form-header">
                <Col lg={8}>
                  {bom.id ? <h5>Edit BOM</h5> : <h5>Create BOM</h5>}
                </Col>
                <Col lg={4} className="d-flex gap-3 justify-content-end">
                  <Button
                    size="sm"
                    className="btn-save"
                    onClick={handleSubmit}
                  >
                    Save BOM
                  </Button>
                  <Button
                    size="sm"
                    className="btn-cancel"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>

              {/* Added By Farhan Khan on 29-Jan-2026 - BOM Type Selector */}
              <Row className="ibs-edit-form pb-3">
                <Row lg={12}>
                  <Col lg={6}>
                    <Form.Group className="mx-3">
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicBomType"
                      >
                        BOM Type
                      </Form.Label>
                      <Form.Select
                        aria-label="Select BOM type"
                        value={bomType}
                        onChange={(e) => {
                          const newBomType = e.target.value;
                          setBomType(newBomType);
                          setBom({ ...bom, bom_type: newBomType });
                        }}
                        disabled={!!bom.id} // Don't allow changing type after creation
                        required
                      >
                        <option value="STANDARD">Bill of Material</option>
                        <option value="MOUNTING_STRUCTURE">Mounting Structure</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {bomType === 'STANDARD'
                          ? 'Bill of Material for product catalog'
                          : 'Mounting structure for solar panel installations'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Row>

              {/* Conditional rendering based on BOM type */}
              {bomType === 'STANDARD' && (
                <>
                  {/* Bill of Material Form - BOM Name removed from UI, kept internally for save */}
                  <Row className="ibs-edit-form pb-3 ">
                    <Row lg={12}>
                      <Col lg={6}>
                        <Form.Group className="mx-3">
                          <Form.Label
                            className="form-view-label"
                            htmlFor="formBasicStatus"
                          >
                            Status
                          </Form.Label>
                          <Form.Select
                            aria-label="Select status"
                            name="status"
                            value={bom.status}
                            onChange={handleBomChange}
                            required
                          >
                            <option value="">--Select--</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Please provide status.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Row>
                </>
              )}

              {bomType === 'MOUNTING_STRUCTURE' && (
                <MountingStructureForm
                  bom={bom}
                  items={items}
                  onBomChange={setBom}
                  onItemsChange={setItems}
                />
              )}

              {/* BOM Line Items Section - Bill of Material */}
              {bomType === 'STANDARD' && (
                <Row className="mt-4">
                  <Col lg={12}>
                    <Row className="view-form-header">
                      <Col lg={8}>
                        <h5>Bill of Material Line Items</h5>
                      </Col>
                      <Col lg={4} className="d-flex justify-content-end">
                        <BootstrapButton
                          size="sm"
                          // className="btn-create"
                          variant="success"
                          onClick={addItemRow}
                        >
                          Add Row
                        </BootstrapButton>
                      </Col>
                    </Row>

                    <div
                      className="my-3"
                      style={{
                        overflowX: "auto",
                        width: "100%",
                      }}
                    >
                      <Table
                        striped
                        className="shadow rounded sm"
                        style={{
                          minWidth: "1450px", // force horizontal scroll only here
                        }}
                      >
                        <thead className="bg-custom text-white">
                          <tr>
                            <th style={{ width: 60 }}>S.No</th>
                            <th style={{ minWidth: 180 }}>Products</th>
                            <th style={{ minWidth: 160 }}>Specification</th>
                            <th style={{ minWidth: 220 }}>Category</th>
                            <th style={{ minWidth: 220 }}>SIZE / RANGE / CAPACITY</th>
                            <th style={{ minWidth: 130 }}>Unit</th>
                            <th style={{ minWidth: 120 }}>Quantity</th>
                            <th style={{ minWidth: 200 }}>Manufacturer Warranty</th>
                            <th style={{ minWidth: 160 }}>Brand</th>
                            <th style={{ minWidth: 100 }}>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="text-center">
                                No items added. Click "Add Row" to add items.
                              </td>
                            </tr>
                          ) : (
                            items.map((item, index) => (
                              <tr key={item.id || index}>
                                <td className="text-center align-middle">
                                  {item.s_no ?? index + 1}
                                </td>

                                {/* Product */}
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    onChange={(selected) =>
                                      handleProductChange(index, selected)
                                    }
                                    options={productOptions}
                                    value={productOptions.find(
                                      (p) => p.value === item.product_id
                                    )}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    className={
                                      itemErrors[`${index}_product_id`]
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                </td>

                                {/* Specification */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={item.specification || ""}
                                    onChange={(e) =>
                                      handleItemChange(
                                        index,
                                        "specification",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>

                                {/* Category - Multi-Select */}
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    isMulti
                                    onChange={(selected) =>
                                      handleItemChange(
                                        index,
                                        "category",
                                        selected && selected.length > 0
                                          ? selected.map((s) => s.value).join(",")
                                          : ""
                                      )
                                    }
                                    options={BOM_CATEGORY_OPTIONS}
                                    value={
                                      item.category
                                        ? item.category
                                          .split(",")
                                          .filter(Boolean)
                                          .map((v) =>
                                            BOM_CATEGORY_OPTIONS.find(
                                              (c) => c.value === v.trim()
                                            )
                                          )
                                          .filter(Boolean)
                                        : []
                                    }
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />
                                </td>

                                {/* Size */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={item.size_range_capacity || ""}
                                    onChange={(e) =>
                                      handleItemChange(
                                        index,
                                        "size_range_capacity",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>

                                {/* Unit */}
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    onChange={(selected) =>
                                      handleUnitChange(index, selected)
                                    }
                                    options={unitOptions}
                                    value={unitOptions.find(
                                      (u) => u.value === item.unit
                                    )}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    className={
                                      itemErrors[`${index}_unit`]
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                </td>

                                {/* Quantity */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    step={0.0001}
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      const num = raw !== "" ? parseFloat(raw) : null;
                                      handleItemChange(index, "quantity", !isNaN(num) ? num : raw);
                                    }}
                                  />
                                </td>

                                {/* Warranty */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={item.manufacturer_warranty || ""}
                                    onChange={(e) =>
                                      handleItemChange(
                                        index,
                                        "manufacturer_warranty",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>

                                {/* Brand */}
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    onChange={(selected) =>
                                      handleBrandChange(index, selected)
                                    }
                                    options={brandOptions}
                                    value={brandOptions.filter((b) =>
                                      toBrandIdsArr(item.brand_id).includes(b.value)
                                    )}
                                    isMulti
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    className={
                                      itemErrors[`${index}_brand_id`]
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                </td>

                                {/* Action */}
                                <td>
                                  <BootstrapButton
                                    size="sm"
                                    variant="danger"
                                    onClick={() => removeItemRow(index)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </BootstrapButton>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
              )}
            </Form>
          </Col>
          <Col></Col>
        </Row>
      </Container>
    </Main>
  );
};

export default BomEdit;

